import { Checkbox, FormControlLabel } from '@mui/material';
import { CheckboxAdapter } from 'components/enhanced/form/adapters/CheckboxAdapter';
import { DatePickerAdapter } from 'components/enhanced/form/adapters/date-time/DatePickerAdapter';
import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { required } from 'components/helpers/validators';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  costElementService,
  equipmentService,
  examService,
  laboratoryService,
  macroExamService,
  workstationService
} from 'shared/services';
import { operationalUnitService } from 'features/settings/topology/api/operational-unit.service';
import { Option } from 'shared/types/general';
import { ICostElement, IPeriodicCostElemet } from '../types';
import { sectorService } from 'features/settings/topology/api/sector.service';
import { SectionHeader } from 'components/ui/typography';

const timeUnits = [
  { id: 1, code: 'Days' },
  { id: 2, code: 'Weeks' },
  { id: 3, code: 'Months' },
  { id: 4, code: 'Years' }
];

interface CostElementFormProps {
  onSubmit: any;
  costElement: ICostElement | IPeriodicCostElemet;
  periodical?: boolean;
}
function CostElementForm({ onSubmit, costElement, periodical }: CostElementFormProps) {
  const { t } = useTranslation();

  const [layerTypes, setLayerTypes] = useState<any>([]);
  const [isPeriodical, setIsPeriodical] = useState<boolean>(!!periodical);
  const [layerType, setLayerType] = useState<Option>({ id: null, code: null });
  const [layers, setLayers] = useState<Option[]>([]);

  useEffect(() => {
    costElementService.getLayerOptions().then(setLayerTypes);
  }, []);

  useEffect(() => {
    switch (layerType['model']) {
      case 'laboratory':
        laboratoryService.getAllBaseInformation().then((res) => {
          setLayers(
            res.map((item) => {
              return {
                id: item.id,
                code: item.description
              };
            })
          );
        });
        break;
      case 'operationalunit':
        operationalUnitService.getAllBaseInformation().then((res) => {
          setLayers(
            res.map((item) => {
              return {
                id: item.id,
                code: item.description
              };
            })
          );
        });
        break;
      case 'sector':
        sectorService.getAllBaseInformation().then((res) => {
          setLayers(
            res.map((item) => {
              return {
                id: item.id,
                code: item.description
              };
            })
          );
        });
        break;
      case 'workstation':
        workstationService.getAllBaseInformation().then((res) => {
          setLayers(
            res.map((item) => {
              return {
                id: item.id,
                code: item.description
              };
            })
          );
        });
        break;
      case 'equipment':
        equipmentService.getAllBaseInformation().then((res) => {
          setLayers(
            res.map((item) => {
              return {
                id: item.id,
                code: item.description
              };
            })
          );
        });
        break;
      case 'exam':
        examService.getAllBaseInformation().then((res) => {
          setLayers(
            res.map((item) => {
              return {
                id: item.id,
                code: item.description
              };
            })
          );
        });
        break;
      case 'macroexam':
        macroExamService.getAllBaseInformation().then((res) => {
          setLayers(
            res.map((item) => {
              return {
                id: item.id,
                code: item.description
              };
            })
          );
        });
        break;
    }
  }, [layerType]);

  return (
    <div>
      {costElement && (
        <EnhancedForm
          onSubmit={(values) => onSubmit(values, isPeriodical)}
          disableEdit={!!costElement.id}
          autoStyleLayout={true}
          columns={5}>
          <IdentityField
            name="code"
            defaultValue={costElement.code}
            component={TextFieldAdapter}
            label={t('global.code')}
            type="text"
          />

          <IdentityField
            gridSpan={2}
            name="description"
            defaultValue={costElement.description}
            component={TextFieldAdapter}
            label={t('global.description')}
            type="text"
          />

          <FormControlLabel
            label={t('entities.cost_element.periodical')}
            control={
              <Checkbox onClick={() => setIsPeriodical(!isPeriodical)} checked={isPeriodical} />
            }
          />

          <IdentityField
            name="active"
            initialValue={costElement.active}
            defaultValue={costElement.active}
            component={CheckboxAdapter}
            label={t('global.active')}
            type="checkbox"
          />

          <IdentityField
            name="cost"
            defaultValue={+costElement.cost}
            component={TextFieldAdapter}
            label={t('entities.cost_element.cost')}
            type="number"
          />

          <IdentityField
            gridSpan={2}
            name="content_type"
            defaultValue={costElement.content_type}
            initValue={costElement.content_type}
            component={AutocompleteAdapter}
            options={layerTypes}
            optionLabel="model"
            label={t('entities.cost_element.content_type')}
            type="number"
            setValue={setLayerType}
          />

          <IdentityField
            gridSpan={2}
            name="object_id"
            defaultValue={costElement.object_id}
            initValue={costElement.object_id}
            component={AutocompleteAdapter}
            options={layers}
            label={t('entities.cost_element.object_id')}
            type="number"
          />

          {!isPeriodical && (
            <>
              <SectionHeader title={t('entities.cost_element.single')} gridSpan className="mt-3" />
              <IdentityField
                name="date"
                defaultValue={costElement['date']}
                component={DatePickerAdapter}
                label={t('entities.cost_element.date')}
                type="date"
                validate={required}
              />
            </>
          )}
          {isPeriodical && (
            <>
              <SectionHeader
                title={t('entities.cost_element.periodical')}
                gridSpan
                className="mt-3"
              />
              <IdentityField
                name="time_unit"
                defaultValue={costElement['time_unit']}
                component={AutocompleteAdapter}
                options={timeUnits}
                label={t('entities.cost_element.time_unit')}
                type="text"
              />
              <IdentityField
                name="frequency_units"
                defaultValue={costElement['frequency_units']}
                component={TextFieldAdapter}
                label="Frequency"
                type="number"
              />
              <IdentityField
                name="date_start"
                defaultValue={costElement['date_start']}
                component={DatePickerAdapter}
                label={t('entities.cost_element.date_start')}
                type="date"
                validate={required}
              />
              <IdentityField
                name="date_end"
                component={DatePickerAdapter}
                label={t('entities.cost_element.date_end')}
                type="date"
                validate={required}
              />
            </>
          )}
        </EnhancedForm>
      )}
    </div>
  );
}

export default CostElementForm;
